import { throwErrorWithStack } from "../crashlytics";
import { menuRmAdsBtnEl } from "../elements";
import { handleLivesModal, updateLives } from "../lives";
import { inAppPurchases } from "../plugins";
import getSymbolFromCurrency from "currency-symbol-map";
import { showErrorSnackbar, showSuccessSnackbar } from "../snackbar";
import { BOOSTER_CELLS_NAMES, BOOSTER_CELLS_NAMES_EXTERNAL } from "../cell";
import { logEvent } from "../analytics";
import { withSpinner } from "../spinner";
import store from "../store";
import { IS_WEB } from "../config";
import { getLogger } from "../logging";

const log = getLogger("🛒 IAP");

function saveProductInfoToLocalStorage(product) {
  const { productId, title, description, currency, price, priceAsDecimal } =
    product;
  store.set(`title_${productId}`, title);
  store.set(`description_${productId}`, description);
  store.set(`currency_${productId}`, currency);
  store.set(`price_${productId}`, price);
  store.set(`priceAsDecimal_${productId}`, priceAsDecimal);
}

export async function purchase(productId, consume = false) {
  if (IS_WEB) {
    showErrorSnackbar("Purchases are not available on the web");
    return;
  }
  if (inAppPurchases === undefined) return;
  try {
    const data = await inAppPurchases.purchase(productId);
    log.info(`Purchase: ${JSON.stringify(data)}`);
    if (data.pending) return true; // not paid for yet, complete later
    const dataComplete = await inAppPurchases.completePurchase(
      productId,
      consume
    );
    log.info(`Complete purchase: ${JSON.stringify(dataComplete)}`);
    return dataComplete.quantity;
  } catch (error) {
    switch (error.responseCode) {
      case 1: // https://developer.android.com/reference/com/android/billingclient/api/BillingClient.BillingResponseCode#USER_CANCELED
        return false;
      //   case 6: // https://developer.android.com/reference/com/android/billingclient/api/BillingClient.BillingResponseCode#ERROR
      //     retry
      case 7: // https://developer.android.com/reference/com/android/billingclient/api/BillingClient.BillingResponseCode#ITEM_ALREADY_OWNED
        log.warn("🛒 Already purchased, consuming...");
        try {
          const dataComplete = await inAppPurchases.completePurchase(
            productId,
            consume
          );
          log.info(`Complete purchase: ${JSON.stringify(dataComplete)}`);
        } catch (error) {
          throwErrorWithStack(error);
        }
        return false;
      default:
        throwErrorWithStack(error);
    }
  }
  return false;
}

export function setIAPInfo(productIds) {
  if (typeof productIds === "string" || productIds instanceof String)
    productIds = [productIds];
  productIds.forEach((productId) => {
    const price = store[`price_${productId}`];
    const priceAsDecimal = store[`priceAsDecimal_${productId}`];
    const currency = store[`currency_${productId}`];

    if (!price) {
      log.warn(`No price for product ${productId} in store`);
      return;
    }
    const clsName = `price_${productId}`;
    Array.from(document.getElementsByClassName(clsName)).forEach((el) => {
      el.innerHTML = price;
    });
    Array.from(
      document.getElementsByClassName(`price_${productId}_per_life`)
    ).forEach((el) => {
      el.innerHTML = `${getSymbolFromCurrency(currency) || currency}${
        Math.round((parseFloat(priceAsDecimal) / 9) * 100) / 100
      }`;
    });
  });
}

export async function getIAPInfo(productIds, retryCount = 0) {
  if (inAppPurchases === undefined) return;
  if (typeof productIds === "string" || productIds instanceof String)
    productIds = [productIds];
  try {
    const products = await inAppPurchases.getAllProductInfo(productIds);
    if (products.length != productIds.length)
      throw new Error(
        `Expected ${productIds.length} products but got ${products.length} instead`
      );
    products.forEach(saveProductInfoToLocalStorage);
    return products;
  } catch (error) {
    if (error.responseCode == 6 && retryCount < 5) {
      // retry with exponential backoff
      const delay = Math.pow(2, retryCount) * 1000;
      log.warn(
        `Failed to get IAP info, retrying in ${delay}ms. Error: ${error.message}`
      );
      setTimeout(getIAPInfo, delay, productIds, retryCount + 1);
    } else throwErrorWithStack(error);
  }
}

export async function fillUpLives() {
  logEvent("initiate_purchase_fill_up_lives");
  const success = await withSpinner(purchase("fill_up_lives", true));
  if (!success) return;
  store.set("lastLifeLostDate", new Date());
  store.set("numLives", Math.max(9, store.numLives));
  handleLivesModal();
  updateLives();
  showSuccessSnackbar("You got 9 lives back! 🎉");
  logEvent("purchase_fill_up_lives");
}

export async function removeAds() {
  logEvent("initiate_purchase_remove_ads");
  const success = await withSpinner(purchase("rm_ads"));
  if (!success) {
    log.info("🛒 Didn't remove ads");
    return;
  }
  log.info("🛒 Removed ads");
  store.set("removedAds", true);
  handleAds();
  logEvent("purchase_remove_ads");
}

export function handleAds() {
  if (store.removedAds) menuRmAdsBtnEl.style.display = "none";
  else if (!IS_WEB || __DEV__) menuRmAdsBtnEl.style.display = "block";
}

export async function getAndSetIAPInfo(productIds) {
  await getIAPInfo(productIds);
  await setIAPInfo(productIds);
}

export const purchaseBooster = async (boosterState) => {
  const boosterName = BOOSTER_CELLS_NAMES[boosterState];
  logEvent("initiate_purchase_booster", { name: boosterName });
  const quantity = await withSpinner(
    purchase(`booster_${boosterName.toLowerCase()}_single`, true)
  );
  if (!quantity) return;
  const boosterKey = `num${boosterName}Boosters`;
  store.set(boosterKey, store[boosterKey] + quantity);
  const boosterNameExternal = BOOSTER_CELLS_NAMES_EXTERNAL[boosterState];
  showSuccessSnackbar(
    quantity == 1
      ? `Sweet! You've stocked up on a new ${boosterNameExternal} Booster! 🎉`
      : `Sweet! You've stocked up ${quantity} new ${boosterNameExternal} Boosters! 🎉`
  );
  logEvent("purchase_booster", {
    name: boosterName,
    quantity: quantity,
  });
};
